/**
 * Route helpers for named route support in React Router
 * Provides mapping between route names and path patterns
 */
import { RouteDefinition } from "./types";
import { generatePath, matchPath } from "react-router-dom";

/**
 * Result of building route maps
 */
export interface RouteMaps {
  /** Map of route names to path patterns */
  pathMap: Map<string, string>;
  /** Map of route names to their default parameters */
  defaultParamsMap: Map<string, Record<string, any>>;
}

/**
 * Build flat maps of route names to path patterns and default parameters
 * Handles nested routes like "pages.custom" → "/pages/:id/:slug"
 *
 * @param routes - Array of route definitions
 * @param parentName - Parent route name for nested routes
 * @param parentPath - Parent path for nested routes
 * @param parentDefaultParams - Default params inherited from parent routes
 * @returns Object containing pathMap and defaultParamsMap
 */
export function buildRouteMaps(
  routes: RouteDefinition[],
  parentName = "",
  parentPath = "",
  parentDefaultParams: Record<string, any> = {},
): RouteMaps {
  const pathMap = new Map<string, string>();
  const defaultParamsMap = new Map<string, Record<string, any>>();

  for (const route of routes) {
    const fullName = parentName ? `${parentName}.${route.name}` : route.name;
    // Remove query params from path (e.g., "?:delete" → "")
    const cleanPath = route.path.split("?")[0];
    const fullPath = parentPath + cleanPath;

    // Merge parent defaultParams with route's own defaultParams
    const mergedDefaultParams = {
      ...parentDefaultParams,
      ...(route.defaultParams || {}),
    };

    pathMap.set(fullName, fullPath);

    // Only store defaultParams if there are any
    if (Object.keys(mergedDefaultParams).length > 0) {
      defaultParamsMap.set(fullName, mergedDefaultParams);
    }

    // Recursively process children
    if (route.children) {
      const childMaps = buildRouteMaps(
        route.children,
        fullName,
        fullPath,
        mergedDefaultParams,
      );

      childMaps.pathMap.forEach((path, name) => {
        pathMap.set(name, path);
      });

      childMaps.defaultParamsMap.forEach((params, name) => {
        defaultParamsMap.set(name, params);
      });
    }
  }

  return { defaultParamsMap, pathMap };
}

/**
 * Build a query string from parameters
 * Filters out undefined/null values and path parameters
 *
 * @param params - Parameters to convert to query string
 * @param pathPattern - Path pattern to identify path params (e.g., ":id")
 * @returns Query string without leading "?" or empty string if no params
 */
function buildQueryString(
  params: Record<string, any>,
  pathPattern: string,
): string {
  // Extract path parameter names from the pattern (e.g., ":id" → "id")
  const pathParamNames = new Set(
    (pathPattern.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || []).map((param) =>
      param.slice(1),
    ),
  );

  // Filter to only query params (not path params) and non-null values
  const queryParams = Object.entries(params).filter(
    ([key, value]) =>
      !pathParamNames.has(key) && value !== undefined && value !== null,
  );

  if (queryParams.length === 0) {
    return "";
  }

  return queryParams
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
}

/**
 * Build a path with parameters filled in
 * Merges defaultParams with provided params and appends query string
 *
 * @example buildPathWithParams("pages.custom", {id: 123, slug: "my-page"}, routeMap, defaultParamsMap)
 *   → "/pages/123/my-page?siteType=onlineInvitation"
 */
export function buildPathWithParams(
  routeName: string,
  params: Record<string, any>,
  routeMap: Map<string, string>,
  defaultParamsMap: Map<string, Record<string, any>> = new Map(),
): string {
  const pathPattern = routeMap.get(routeName) || null;

  if (!pathPattern) {
    console.warn(
      `[RouteMap] No path pattern found for route "${routeName}". Using fallback.`,
    );
    // Fallback: convert route name to path
    if (routeName.includes(".")) {
      return "/" + routeName.replace(/\./g, "/");
    }

    return routeName.startsWith("/") ? routeName : "/" + routeName;
  }

  // Merge defaultParams with provided params (provided params take precedence)
  const defaultParams = defaultParamsMap.get(routeName) || {};
  const mergedParams = { ...defaultParams, ...params };

  try {
    const basePath = generatePath(pathPattern, mergedParams);

    // Build query string from merged params
    const queryString = buildQueryString(mergedParams, pathPattern);

    return queryString ? `${basePath}?${queryString}` : basePath;
  } catch (error) {
    // If generatePath fails (missing params), return pattern as-is
    console.warn(
      `Failed to generate path for route "${routeName}" with params`,
      mergedParams,
      error,
    );

    return pathPattern;
  }
}

/**
 * Match a path against the route map to find the route name
 * Uses React Router's matchPath for reliable pattern matching
 * Handles parameterized routes like "/pages/:id/:slug"
 */
export function matchPathToRouteName(
  path: string,
  routeMap: Map<string, string>,
): { name: string; params: Record<string, any> } | null {
  // Iterate through routes and try to match using React Router's matchPath
  for (const [routeName, routePattern] of routeMap.entries()) {
    const match = matchPath(routePattern, path);

    if (match) {
      return {
        name: routeName,
        params: match.params as Record<string, any>,
      };
    }
  }

  return null;
}
